import { useState, useRef, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { useApiQuery, useApiMutation } from "@/lib/api/hooks"
import { api } from "@/lib/api/client"
import { CardRendererRaw } from "@/components/cards/CardRenderer"
import { Button } from "@/components/ui/button"
import { playCorrect, playIncorrect } from "@/lib/sounds"
import { Scratchpad } from "@/components/Scratchpad"

interface CardResult {
  cardId: string
  correct: boolean
  responseTimeMs: number
}

export function ReviewPage() {
  const { topicId } = useParams<{ topicId: string }>()
  const navigate = useNavigate()
  const isGlobal = !topicId
  const queryClient = useQueryClient()
  const { data, isPending, refetch } = useApiQuery(
    "get",
    "/fsrs/review-session",
    { params: { query: { topicId, limit: 8 } } }
  )
  const recordReviewsMutation = useApiMutation("post", "/fsrs/reviews")

  const [currentIndex, setCurrentIndex] = useState(0)
  const [results, setResults] = useState<CardResult[]>([])
  const [answered, setAnswered] = useState(false)
  const [finished, setFinished] = useState(false)
  const [sessionsCompleted, setSessionsCompleted] = useState(0)
  const [freshDueCount, setFreshDueCount] = useState<number | null>(null)
  const cardStartTime = useRef(Date.now())

  const cards = data?.cards ?? []
  const currentCard = cards[currentIndex]
  const totalCards = cards.length
  const correctCount = results.filter((r) => r.correct).length

  const handleAnswer = useCallback(
    (correct: boolean) => {
      if (!currentCard || answered) return
      const elapsed = Date.now() - cardStartTime.current
      setResults((prev) => [
        ...prev,
        { cardId: currentCard.id, correct, responseTimeMs: elapsed },
      ])
      setAnswered(true)

      if (correct) {
        playCorrect()
      } else {
        playIncorrect()
      }
    },
    [currentCard, answered]
  )

  async function handleContinue() {
    if (currentIndex < totalCards - 1) {
      setCurrentIndex((i) => i + 1)
      setAnswered(false)
      cardStartTime.current = Date.now()
    } else {
      await recordReviewsMutation.mutateAsync({ body: { cardResults: results } })
      // Fetch the real due count after reviews are saved.
      const { data: dueData } = await api.GET("/fsrs/due-count")
      setFreshDueCount(dueData?.dueCount ?? 0)
      setFinished(true)
    }
  }

  async function handleReviewMore() {
    setSessionsCompleted((n) => n + 1)
    setCurrentIndex(0)
    setResults([])
    setAnswered(false)
    setFinished(false)
    setFreshDueCount(null)
    cardStartTime.current = Date.now()
    // Invalidate so the next session reflects updated FSRS state
    await queryClient.invalidateQueries({ queryKey: ["get", "/fsrs/review-session"] })
    await queryClient.invalidateQueries({ queryKey: ["get", "/fsrs/due-count"] })
    await refetch()
  }

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    )
  }

  // Nothing to review
  if (!data || cards.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-sm w-full text-center space-y-4 px-4">
          <h2 className="text-xl font-bold">Nothing to review yet</h2>
          <p className="text-sm text-muted-foreground">
            {isGlobal
              ? "Complete a lesson first — once you've answered some cards, they'll show up here for review."
              : `All cards in ${data?.topicName ?? "this topic"} are fresh.`}
          </p>
          <Button
            variant="outline"
            onClick={() => (isGlobal ? navigate("/") : navigate(-1))}
          >
            {isGlobal ? "Back to dashboard" : "Back to topics"}
          </Button>
        </div>
      </div>
    )
  }

  // Review complete
  if (finished) {
    const score = results.length > 0 ? Math.round((correctCount / results.length) * 100) : 0
    const remaining = freshDueCount ?? 0
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-sm w-full text-center space-y-6 px-4">
          <h2 className="text-2xl font-bold">Review Complete</h2>
          {data.topicName && (
            <p className="text-muted-foreground">{data.topicName}</p>
          )}
          <div className="text-5xl font-bold tabular-nums">
            {score}%
          </div>
          <p className="text-sm text-muted-foreground">
            {correctCount} of {results.length} correct
          </p>
          {sessionsCompleted > 0 && (
            <p className="text-xs text-muted-foreground">
              {sessionsCompleted + 1} sessions this visit
            </p>
          )}
          {isGlobal && remaining > 0 && (
            <p className="text-sm">
              <span className="font-semibold">{remaining}</span> {remaining === 1 ? "card" : "cards"} still due
            </p>
          )}
          <div className="flex flex-col gap-2 pt-4">
            {isGlobal && remaining > 0 && (
              <Button onClick={handleReviewMore}>
                Review more
              </Button>
            )}
            <Button
              variant={isGlobal && remaining > 0 ? "outline" : "default"}
              onClick={async () => {
                await queryClient.invalidateQueries({ queryKey: ["get", "/fsrs/due-count"] })
                await queryClient.invalidateQueries({ queryKey: ["get", "/fsrs/review-session"] })
                isGlobal ? navigate("/") : navigate(-1)
              }}
            >
              {isGlobal ? "Done for now" : "Back to topics"}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border px-4 py-3 flex items-center gap-4 shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Exit
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {isGlobal ? "Daily Review" : `Review: ${data.topicName ?? "Review Session"}`}
          </p>
        </div>
        <span className="text-xs text-muted-foreground tabular-nums shrink-0">
          {currentIndex + 1} / {totalCards}
        </span>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-muted shrink-0">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / totalCards) * 100}%` }}
        />
      </div>

      <main className="flex-1 overflow-y-auto">
        <div className="relative max-w-2xl mx-auto px-4 py-8">
          <Scratchpad resetKey={currentCard?.id} />
          {currentCard && (
            <CardRendererRaw
              key={currentCard.id}
              type={currentCard.type}
              data={currentCard.data as Record<string, unknown>}
              onAnswer={handleAnswer}
            />
          )}
        </div>
      </main>

      {/* Bottom bar */}
      <div className="border-t border-border px-4 py-4 shrink-0">
        <div className="max-w-2xl mx-auto">
          {answered && (
            <Button className="w-full" onClick={handleContinue}>
              {currentIndex < totalCards - 1 ? "Continue" : "Finish"}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
